import { z } from "zod";

/** Calendar date in `YYYY-MM-DD` form. Plain strings avoid time-zone drift for all-day bookings. */
export const isoDateSchema = z.iso.date();
export type IsoDate = string;

export interface DateRange {
  startDate: IsoDate;
  endDate: IsoDate;
}

const MS_PER_DAY = 86_400_000;

const toUtcMs = (date: IsoDate): number => {
  const ms = Date.parse(`${date}T00:00:00Z`);
  if (Number.isNaN(ms)) {
    throw new Error(`Invalid ISO date: ${date}`);
  }
  return ms;
};

const fromUtcMs = (ms: number): IsoDate =>
  new Date(ms).toISOString().slice(0, 10);

export const addDays = (date: IsoDate, days: number): IsoDate =>
  fromUtcMs(toUtcMs(date) + days * MS_PER_DAY);

/** Whole days from `from` to `to` (negative when `to` is earlier). */
export const daysBetween = (from: IsoDate, to: IsoDate): number =>
  Math.round((toUtcMs(to) - toUtcMs(from)) / MS_PER_DAY);

/** Number of calendar days in an inclusive range. */
export const rangeLengthDays = ({ startDate, endDate }: DateRange): number =>
  daysBetween(startDate, endDate) + 1;

export const eachDay = ({ startDate, endDate }: DateRange): IsoDate[] => {
  const days: IsoDate[] = [];
  for (let d = startDate; d <= endDate; d = addDays(d, 1)) {
    days.push(d);
  }
  return days;
};

/** Inclusive ranges overlap when each starts on or before the other ends. */
export const rangesOverlap = (a: DateRange, b: DateRange): boolean =>
  a.startDate <= b.endDate && b.startDate <= a.endDate;

export const isDateInRange = (date: IsoDate, range: DateRange): boolean =>
  range.startDate <= date && date <= range.endDate;

/** Today's calendar date as observed in an IANA time zone. */
export const todayInTimeZone = (
  timeZone: string,
  now: Date = new Date()
): IsoDate => {
  const parts = new Intl.DateTimeFormat("en-US", {
    timeZone,
    year: "numeric",
    month: "2-digit",
    day: "2-digit",
  }).formatToParts(now);
  const part = (type: Intl.DateTimeFormatPartTypes): string =>
    parts.find((p) => p.type === type)?.value ?? "";
  return `${part("year")}-${part("month")}-${part("day")}`;
};

export const isValidTimeZone = (timeZone: string): boolean => {
  try {
    new Intl.DateTimeFormat("en-US", { timeZone });
    return true;
  } catch {
    return false;
  }
};

export interface YearMonth {
  year: number;
  /** 1-12 */
  month: number;
}

export const yearMonthOf = (date: IsoDate): YearMonth => ({
  year: Number(date.slice(0, 4)),
  month: Number(date.slice(5, 7)),
});

export const shiftMonth = (
  { year, month }: YearMonth,
  delta: number
): YearMonth => {
  const zeroBased = year * 12 + (month - 1) + delta;
  return { year: Math.floor(zeroBased / 12), month: (zeroBased % 12) + 1 };
};

export const firstOfMonth = ({ year, month }: YearMonth): IsoDate =>
  `${String(year).padStart(4, "0")}-${String(month).padStart(2, "0")}-01`;

export const lastOfMonth = (ym: YearMonth): IsoDate =>
  addDays(firstOfMonth(shiftMonth(ym, 1)), -1);

/** Sunday-first weeks covering the month, padded with adjacent-month days. */
export const monthGrid = (ym: YearMonth): IsoDate[][] => {
  const first = firstOfMonth(ym);
  const last = lastOfMonth(ym);
  const leading = new Date(toUtcMs(first)).getUTCDay();
  const trailing = 6 - new Date(toUtcMs(last)).getUTCDay();
  const days = eachDay({
    startDate: addDays(first, -leading),
    endDate: addDays(last, trailing),
  });
  const weeks: IsoDate[][] = [];
  for (let i = 0; i < days.length; i += 7) {
    weeks.push(days.slice(i, i + 7));
  }
  return weeks;
};
